import { Link } from 'react-router-dom';
import { IconUtensils, IconLane, IconWaves, IconTheater, IconHome, IconLandscape, IconPin, IconLeaf } from '../components/Icons';
import useScrollReveal from '../hooks/useScrollReveal';
import './EcoVillage.css';

const pillars = [
  { Icon: IconUtensils, title: 'Local Dining', desc: 'Food and beverages can be served at your terrace villa or the Eco-Café.' },
  { Icon: IconLane, title: 'Eco-Sophy Lane', desc: 'Walk around the Eco-Village and venture into the Eco-sophy Lane. Reflect and contemplate on whats truly important in life.' },
  { Icon: IconWaves, title: 'River Access', desc: 'Step outside, take a dip and rejuvenate yourself. Feel the gentle warmth of the river passing through your feet as you play in the river.' },
  { Icon: IconTheater, title: 'Amphitheater', desc: 'A multi-purpose amphitheater is provided either for outdoor classrooms or as a place for guests to congregate.' },
  { Icon: IconHome, title: 'Riverfront Villas', desc: 'All the wooden villas face the Pentung River, where huge boulders lay amid the slow current of the water.' },
  { Icon: IconLandscape, title: 'Village Life', desc: 'Interact with the friendly local villagers, who would normally walk pass the Eco-Village to find grass for their cattle or to catch fish in the river.' },
];

export default function EcoVillage() {
  useScrollReveal();
  return (
    <main>
      {/* Hero */}
      <section className="eco-hero" aria-labelledby="eco-heading">
        <div className="eco-hero__bg" />
        <div className="container eco-hero__content reveal reveal-up">
          <span className="section-label" style={{ color: '#86efac' }}>Latest Initiative</span>
          <h1 id="eco-heading">Nglanggeran<br />Eco-Village</h1>
          <p className="eco-hero__tagline">
            Nglanggeran Eco-Village offers a splendid getaway from the hustle and bustle of hypermodern,<br />
            materialistic city life that gives no meaning and purpose in our existence.
          </p>
          <div className="eco-hero__meta">
            <span><IconPin size={13} color="rgba(255,255,255,0.7)" /> Gunung Kidul, Yogyakarta</span>
            <span><IconLeaf size={13} color="rgba(255,255,255,0.7)" /> Est. 2025</span>
            <span>Community-led Initiative</span>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="section" aria-labelledby="eco-vision-heading">
        <div className="container">
          <div className="eco-vision__grid">
            <div className="reveal reveal-up">
              <span className="section-label">Our Vision</span>
              <h2 id="eco-vision-heading" className="section-title">Listen closely to the river and awaken the soul in you</h2>
              <p>Nglanggeran Eco-Village sits on the edge of Pentung River in Pathuk, Gunung Kidul, which is only 45 minutes away from the City of Yogyakarta. We offer 10 rustic designed, wooden villas that would surely inspire your creativity and bring you to your wildest imagination about beauty.</p>
              <p>It is a tranquil place for an authentic and true experience of living in a Javanese village. Bring your friends, family, relatives and business associates for a meeting, gathering, reunion, team building, party, wedding, or simply to hangout and enjoy a cup of coffee and a decent conversation.</p>
              
              <div style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
                <Link to="/contact" className="btn btn-primary btn-sm">Inquire Now</Link>
              </div>

              <div className="eco-vision__stats">
                {[{ v: '10', l: 'Villas' }, { v: '0', l: 'City Noise' }, { v: '1', l: 'River' }, { v: 'Unlimited', l: 'Reasons to comeback' }].map(s => (
                  <div key={s.l} className="eco-vision__stat">
                    <strong>{s.v}</strong><span>{s.l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="eco-vision__img-wrap reveal reveal-photo">
              <img src="/ecovillage/5.jpeg" alt="Nglanggeran Eco-Village" className="eco-vision__img" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="section section-alt" aria-labelledby="eco-pillars-heading">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '3rem' }}>
            <span className="section-label">Guest Experience</span>
            <h2 id="eco-pillars-heading" className="section-title">Amenities & Facilities</h2>
          </div>
          <div className="grid-3">
            {pillars.map((p, i) => (
              <div key={p.title} className={`eco-pillar reveal reveal-up delay-${(i % 3) + 1}`}>
                <div className="eco-pillar__icon"><p.Icon size={32} color="currentColor" /></div>
                <h3 className="eco-pillar__title">{p.title}</h3>
                <p className="eco-pillar__desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo mosaic */}
      <section className="eco-gallery" aria-label="Eco-Village photo mosaic">
        <div className="eco-gallery__mosaic">
          {[
            '/ecovillage/21.jpeg',
            '/ecovillage/26.jpeg',
            '/ecovillage/39.jpeg',
            '/ecovillage/49.jpeg',
            '/ecovillage/56.jpeg',
            '/ecovillage/59.jpeg',
          ].map((src, i) => (
            <div key={i} className={`eco-gallery__img eco-gallery__img--${i} reveal reveal-photo delay-${(i % 3) + 1}`}>
              <img src={src} alt={`Eco-Village landscape ${i + 1}`} loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* Eco-Village Highlight */}
      <section className="section section-alt" aria-labelledby="eco-village-heading">
        <div className="container">
          <div className="eco-vision__grid" style={{ alignItems: 'center' }}>
            <div className="eco-vision__img-wrap eco-vision__img-wrap--reverse">
              <img src="/ecovillage/40.jpeg" alt="Nglanggeran Eco-Village" className="eco-vision__img" loading="lazy" />
            </div>
            <div className="reveal reveal-up">
              <span className="section-label">Residential Retreat</span>
              <h2 id="eco-village-heading" className="section-title">Nglanggeran Eco-Village</h2>
              <p>There are ten villas for rent in the Nglanggeran Eco-Village, which are mostly made from old, recycled teakwood. Three types of villas are available for guests: A-Frames, Javanese traditional Limasan and Gladak houses. Altogether, they form a community that blend harmoniously with the natural setting of the village.</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <li style={{ display: 'flex', gap: '0.75rem' }}><span style={{ color: 'var(--gold)' }}>✦</span> Riverside A-Frames, Limasan & Gladak traditional villas</li>
                <li style={{ display: 'flex', gap: '0.75rem' }}><span style={{ color: 'var(--gold)' }}>✦</span> Multi-purpose amphitheatre & "Eco-sophy Lane"</li>
                <li style={{ display: 'flex', gap: '0.75rem' }}><span style={{ color: 'var(--gold)' }}>✦</span> Immersive slow living and ecological conservation activities</li>
              </ul>
              <div style={{ marginTop: '2.5rem' }}>
                <a href="https://eco-village.nglanggeran.com/" target="_blank" rel="noopener noreferrer" className="btn btn-gold" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>Visit Eco-Village Website <span>→</span></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="eco-cta reveal reveal-up">
        <div className="container text-center">
          <h2>Interested in the Eco-Village?</h2>
          <p>For research collaborations, educational partnerships, visit requests, or volunteer opportunities, please get in touch.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2rem' }}>
            <Link to="/contact" className="btn btn-gold">Contact for Inquiries</Link>
            <a href="https://eco-village.nglanggeran.com/" target="_blank" rel="noopener noreferrer" className="btn" style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}>Book a Villa</a>
          </div>
        </div>
      </section>
    </main>
  );
}
